// src/services/tornadoGPTOfflineDescriptionService.js

// Lookup data structures
const ModelDataMap = {
    "TGPTO.130.912": { flowRate: 130, diameter: 912, depthUnderInvert: 1920 },
    "TGPTO.200.1015": { flowRate: 200, diameter: 1015, depthUnderInvert: 2400 },
    "TGPTO.320.1218": { flowRate: 320, diameter: 1218, depthUnderInvert: 2880 },
    "TGPTO.465.1520": { flowRate: 465, diameter: 1520, depthUnderInvert: 3200 },
    "TGPTO.730.1824": { flowRate: 730, diameter: 1824, depthUnderInvert: 3840 },
    "TGPTO.1090.2128": { flowRate: 1090, diameter: 2128, depthUnderInvert: 4480 },
    "TGPTO.1200.2131": { flowRate: 1200, diameter: 2131, depthUnderInvert: 4960 },
    "TGPTO.1430.2530": { flowRate: 1430, diameter: 2530, depthUnderInvert: 4800 },
    "TGPTO.1600.2532": { flowRate: 1600, diameter: 2532, depthUnderInvert: 5120 },
    "TGPTO.1900.3032": { flowRate: 1900, diameter: 3032, depthUnderInvert: 5120 },
};

const ChamberDimensionsMap = {
    "525_1": { length: 2610, width: 971.75, height: 800 },
    "600_1": { length: 2610, width: 1110, height: 800 },
    "750_1": { length: 2610, width: 1387.5, height: 800 },
    "900_1": { length: 2610, width: 1665, height: 1125 },
    "1050_1": { length: 2610, width: 1942.5, height: 1312.5 },
    "1200_1": { length: 2610, width: 2220, height: 1500 },
    "1350_1": { length: 2610, width: 2497.5, height: 1687.5 },
    "1500_1": { length: 2610, width: 2775, height: 1875 },
    "1650_1": { length: 2610, width: 3052.5, height: 1875 },
    "1800_1": { length: 2610, width: 3330, height: 2025 },
    "1950_1": { length: 2610, width: 3607.5, height: 2025 },
    "2100_1": { length: 2610, width: 3885, height: 2025 },
    "750_2": { length: 2610, width: 2137.5, height: 800 },
    "900_2": { length: 2610, width: 2565, height: 1125 },
    "1050_2": { length: 2610, width: 2992.5, height: 1312.5 },
    "1200_2": { length: 2610, width: 3420, height: 1500 },
    "1350_2": { length: 2610, width: 3847.5, height: 1687.5 },
    "1500_2": { length: 2610, width: 4275, height: 1875 },
    "1800_2": { length: 2610, width: 5130, height: 2025 },
    "2100_2": { length: 2610, width: 5985, height: 2625 },
    "600_3": { length: 2610, width: 2310, height: 800 },
    "750_3": { length: 2610, width: 2887.5, height: 800 },
    "900_3": { length: 2610, width: 3465, height: 1125 },
    "1050_3": { length: 2610, width: 4042.5, height: 1312.5 },
    "1200_3": { length: 2610, width: 4620, height: 1500 },
    "1500_3": { length: 2610, width: 5775, height: 1875 },
    "1850_3": { length: 2610, width: 7122.5, height: 23312.5 },
    "2100_3": { length: 2610, width: 8085, height: 2625 },
};

/**
 * Generates detailed description for TornadoGPT Offline product
 * @param {Object} modelConfig - Configuration object containing model parameters
 * @returns {string} Formatted description string
 */
export const generateTornadoGPTOfflineDescription = (modelConfig) => {
    // Fetch model data from lookup
    const modelData = ModelDataMap[modelConfig.modelType] || {};
    const flowRate = modelData.flowRate || 0;
    const diameter = modelData.diameter || 0;
    const depthUnderInvertMm = modelData.depthUnderInvert || 0;

    // Calculate total depth
    let totalDepthMm = 0;
    const fsl = parseFloat(modelConfig.finishSurfaceLevel);
    const il = parseFloat(modelConfig.inletIL);
    if (!isNaN(fsl) && !isNaN(il)) {
        totalDepthMm = (fsl - il) * 1000 + depthUnderInvertMm;
    }

    // Fetch chamber dimensions
    const chamberKey = `${modelConfig.inletSize}_${modelConfig.inletQuantity}`;
    const chamberDims = ChamberDimensionsMap[chamberKey] || {};
    const lengthMm = chamberDims.length || 0;
    const widthMm = chamberDims.width || 0;
    const heightMm = chamberDims.height || 0;

    // Build the description sections with bold Unicode labels
    const OfflineTornadoQuoteDescription = `𝗢𝗳𝗳𝗹𝗶𝗻𝗲 𝗧𝗼𝗿𝗻𝗮𝗱𝗼 𝗤𝘂𝗼𝘁𝗲 𝗗𝗲𝘀𝗰𝗿𝗶𝗽𝘁𝗶𝗼𝗻`;
    const partNumber = `𝗣𝗮𝗿𝘁 𝗡𝘂𝗺𝗯𝗲𝗿: ${modelConfig.modelType}`;

    const description = `𝗗𝗲𝘀𝗰𝗿𝗶𝗽𝘁𝗶𝗼𝗻: TORNADO GROSS POLLUTANT TRAP ${diameter}mm DIA x ${Math.round(
        totalDepthMm
    )}mm DEEP, ${flowRate} LPS TREATMENT FLOW`;

    const detailedDescription = `𝗗𝗲𝘁𝗮𝗶𝗹𝗲𝗱 𝗗𝗲𝘀𝗰𝗿𝗶𝗽𝘁𝗶𝗼𝗻:

The Tornado GPT Off-line Gross Pollutant Trap System with Continuous Deflective Separator is an advanced and efficient water treatment capable of handling designed to tackle gross pollutants and sediment in stormwater runoff. The system is equipped with Class D covers, providing protection ensuring durability, safety, and easy access for maintenance personnel. These covers protect against external elements and easy access for maintenance personnel. Gross Pollutant Trap System, making Continuous Defective Separator and secure. Overall, the Tornado GPT Gross Pollutant Trap System with Continuous Deflective Separator is a cutting-edge solution that offers high-performance pollutant removal, contributing to cleaner waterways and improved stormwater management in urban environments.`;

    const bulletPoints = [
        `\t• Model: ${modelConfig.modelType}`,
        `\t• Diameter: ${diameter}mm`,
        `\t• Finish Surface Level: ${modelConfig.finishSurfaceLevel}m`,
        `\t• Inlet IL: ${modelConfig.inletIL}m`,
        `\t• Inlet Size: ${modelConfig.inletSize}mm`,
        `\t• Inlet Quantity: ${modelConfig.inletQuantity}`,
        `\t• Weir Layout: ${modelConfig.weirLayout}mm`,
        `\t• Depth below invert: ${Math.round(depthUnderInvertMm)}mm Screen`,
        `\t• Stainless Steel Continuous Deflective Separator Screen`,
        `\t• 2200mm diameter Removal Concrete Infill Screen Cover`,
        `\t• 2 x 600mm diameter Maintenance Access Openings`,
        `\t• 2 x 600mm diameter Class D Access Covers`,
    ].join("\n");

    const bypassChamber = `Concrete Bypass Chamber with internal weir and 2 x 600mm access openings.`;

    const chamberDetails = [
        `\t• Length: ${Math.round(lengthMm)}mm and 2 x 600mm access openings.`,
        `\t• Width: ${Math.round(widthMm)}mm`,
        `\t• Height: ${Math.round(heightMm)}mm`,
        `\t• Connection Size: ${modelConfig.inletSize}mm.`,
        `\t• Risers supplied to suit.`,
        `\t• Delivered to site: ${modelConfig.city}`,
    ].join("\n");

    // Combine all sections
    return `${OfflineTornadoQuoteDescription}

${partNumber}

${description}

${detailedDescription}

${bulletPoints}

${bypassChamber}

${chamberDetails}`;
};

/**
 * Get model specifications (flow rate, diameter, depth)
 * @param {string} modelType - Model type identifier
 * @returns {Object} Model specifications
 */
export const getModelSpecifications = (modelType) => {
    return ModelDataMap[modelType] || null;
};

/**
 * Get chamber dimensions
 * @param {number} inletSize - Inlet size in mm
 * @param {number} inletQuantity - Number of inlets
 * @returns {Object} Chamber dimensions
 */
export const getChamberDimensions = (inletSize, inletQuantity) => {
    const key = `${inletSize}_${inletQuantity}`;
    return ChamberDimensionsMap[key] || null;
};
